import ContentLayout from '@/components/layout/ContentLayout';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ROUTE } from '@/routes/constants';
import { applyRecalculateScores, archivePreGamificationRecords, getAllGamificationConfigs, getSchoolGamificationStatus, previewPreGamificationRecords, previewRecalculateScores, refreshLeaderboards, updateGamificationConfig, updateSchoolGamification } from '@/services/generatedAPIs';
import { GamificationConfigResponse, PreGamificationPreviewResponse, RecalculateScoresResponse, SchoolGamificationStatusResponse } from '@/services/generatedAPIs.schemas';
import { Archive, Calculator, Pencil, RefreshCw, School } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

const GamificationConfigPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<GamificationConfigResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingConfig, setEditingConfig] = useState<GamificationConfigResponse | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<PreGamificationPreviewResponse | null>(null);
  const [recalculating, setRecalculating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [recalculateResult, setRecalculateResult] = useState<RecalculateScoresResponse | null>(null);
  const [applied, setApplied] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [schools, setSchools] = useState<SchoolGamificationStatusResponse[]>([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [togglingSchoolId, setTogglingSchoolId] = useState<number | null>(null);
  const [togglingLeaderboardSchoolId, setTogglingLeaderboardSchoolId] = useState<number | null>(null);

  const fetchConfigs = async () => {
    try {
      const response = await getAllGamificationConfigs();
      if (response.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      if (response.code === 200 && response.data) {
        setConfigs(response.data);
      }
    } catch (error: any) {
      if (error?.response?.data?.code === 1003 || error?.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      console.error('Error fetching gamification configs', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async () => {
    try {
      const response = await previewPreGamificationRecords();
      if (response.code === 200 && response.data) {
        setPreview(response.data);
      }
    } catch (error) {
      console.error('Error fetching pre-gamification preview', error);
    }
  };

  const fetchSchools = async () => {
    setLoadingSchools(true);
    try {
      const response = await getSchoolGamificationStatus();
      if (response.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      if (response.code === 200 && response.data) {
        setSchools(response.data);
      }
    } catch (error: any) {
      if (error?.response?.data?.code === 1003 || error?.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      console.error('Error fetching school gamification status', error);
    } finally {
      setLoadingSchools(false);
    }
  };

  const handleToggleSchool = async (school: SchoolGamificationStatusResponse) => {
    if (school.id == null) return;
    setTogglingSchoolId(school.id);
    try {
      const response = await updateSchoolGamification({
        schoolId: school.id,
        gamificationEnabled: !school.gamificationEnabled,
        leaderboardEnabled: !!school.leaderboardEnabled,
      });
      if (response.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      if (response.code === 200) {
        toast.success(t('common.success'));
        fetchSchools();
      } else {
        toast.error(response.message || t('common.fail'));
      }
    } catch (error: any) {
      if (error?.response?.data?.code === 1003 || error?.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      console.error('Error toggling school gamification', error);
      toast.error(t('common.fail'));
    } finally {
      setTogglingSchoolId(null);
    }
  };

  const handleToggleLeaderboard = async (school: SchoolGamificationStatusResponse) => {
    if (school.id == null) return;
    setTogglingLeaderboardSchoolId(school.id);
    try {
      const response = await updateSchoolGamification({
        schoolId: school.id,
        gamificationEnabled: !!school.gamificationEnabled,
        leaderboardEnabled: !school.leaderboardEnabled,
      });
      if (response.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      if (response.code === 200) {
        toast.success(t('common.success'));
        fetchSchools();
      } else {
        toast.error(response.message || t('common.fail'));
      }
    } catch (error: any) {
      if (error?.response?.data?.code === 1003 || error?.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      console.error('Error toggling school leaderboard', error);
      toast.error(t('common.fail'));
    } finally {
      setTogglingLeaderboardSchoolId(null);
    }
  };

  useEffect(() => {
    fetchConfigs();
    fetchPreview();
    fetchSchools();
  }, []);

  const handleEdit = (config: GamificationConfigResponse) => {
    setEditingConfig(config);
    setEditValue(config.configValue ?? '');
    setEditDescription(config.description ?? '');
  };

  const handleSave = async () => {
    if (!editingConfig?.id) return;
    setSaving(true);
    try {
      const response = await updateGamificationConfig({
        id: editingConfig.id,
        configValue: editValue,
        description: editDescription,
      });
      if (response.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      if (response.code === 200) {
        toast.success(t('common.success'));
        setEditingConfig(null);
        fetchConfigs();
      } else {
        toast.error(response.message || t('common.fail'));
      }
    } catch (error) {
      console.error('Error updating gamification config', error);
      toast.error(t('common.fail'));
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewRecalculate = async () => {
    setRecalculating(true);
    try {
      const response = await previewRecalculateScores();
      if (response.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      if (response.code === 200 && response.data) {
        setApplied(false);
        setRecalculateResult(response.data);
      } else {
        toast.error(response.message || 'Failed to preview recalculation');
      }
    } catch (error: any) {
      if (error?.response?.data?.code === 1003 || error?.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      console.error('Error previewing recalculation', error);
      toast.error('Failed to preview recalculation');
    } finally {
      setRecalculating(false);
    }
  };

  const handleApplyRecalculate = async () => {
    setApplying(true);
    try {
      const response = await applyRecalculateScores();
      if (response.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      if (response.code === 200) {
        toast.success('Scores recalculated and applied successfully');
        setApplied(true);
        if (response.data) {
          setRecalculateResult(response.data);
        }
      } else {
        toast.error(response.message || 'Failed to apply recalculation');
      }
    } catch (error: any) {
      if (error?.response?.data?.code === 1003 || error?.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      console.error('Error applying recalculation', error);
      toast.error('Failed to apply recalculation');
    } finally {
      setApplying(false);
    }
  };

  const handleRefreshLeaderboards = async () => {
    setRefreshing(true);
    try {
      const response = await refreshLeaderboards();
      if (response.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      if (response.code === 200) {
        toast.success('Leaderboards refreshed successfully');
      } else {
        toast.error(response.message || 'Failed to refresh leaderboards');
      }
    } catch (error: any) {
      if (error?.response?.data?.code === 1003 || error?.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      console.error('Error refreshing leaderboards', error);
      toast.error('Failed to refresh leaderboards');
    } finally {
      setRefreshing(false);
    }
  };

  const handleArchiveRecords = async () => {
    setArchiving(true);
    try {
      const response = await archivePreGamificationRecords();
      if (response.code === 200) {
        toast.success('Pre-gamification records archived successfully');
        fetchPreview();
      } else {
        toast.error(response.message || 'Failed to archive records');
      }
    } catch (error) {
      console.error('Error archiving records', error);
      toast.error('Failed to archive records');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <ContentLayout title="Gamification Config">
      {/* Admin Actions */}
      <div className="flex gap-2 m-2">
        <Button variant="outline" disabled={recalculating} onClick={handlePreviewRecalculate}>
          <Calculator className="h-4 w-4 mr-2" />
          {recalculating ? 'Loading Preview...' : 'Recalculate All Scores'}
        </Button>

        <Button variant="outline" disabled={refreshing} onClick={handleRefreshLeaderboards}>
          <RefreshCw className="h-4 w-4 mr-2" />
          {refreshing ? 'Refreshing...' : 'Refresh Leaderboards'}
        </Button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" disabled={archiving}>
              <Archive className="h-4 w-4 mr-2" />
              {archiving ? 'Archiving...' : 'Archive Pre-Gamification Records'}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Archive Pre-Gamification Records</AlertDialogTitle>
            </AlertDialogHeader>
            <p className="text-sm text-muted-foreground">
              This will archive all point records before the gamification start time. This action cannot be undone. Are you sure?
            </p>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleArchiveRecords}>Confirm</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Config Table */}
      <div className="p-4 bg-muted m-2 rounded-2xl">
        {loading ? (
          <div className="flex justify-center p-8 text-muted-foreground">Loading...</div>
        ) : configs.length === 0 ? (
          <div className="flex justify-center p-8 text-muted-foreground">No configs found</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Key</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Updated At</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {configs.map((config) => (
                <TableRow key={config.id}>
                  <TableCell className="font-medium">{config.configKey}</TableCell>
                  <TableCell>{config.configValue}</TableCell>
                  <TableCell>{config.dataType}</TableCell>
                  <TableCell className="max-w-xs truncate">{config.description}</TableCell>
                  <TableCell>{config.updatedAt}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(config)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* School Gamification Status */}
      <div className="p-4 bg-muted m-2 rounded-2xl">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <School className="h-5 w-5" />
          School Gamification Status
        </h3>
        {loadingSchools ? (
          <div className="flex justify-center p-8 text-muted-foreground">Loading...</div>
        ) : schools.length === 0 ? (
          <div className="flex justify-center p-8 text-muted-foreground">No schools found</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>School Name</TableHead>
                <TableHead>School Name (EN)</TableHead>
                <TableHead>Gamification</TableHead>
                <TableHead></TableHead>
                <TableHead>Leaderboard</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schools.map((school) => (
                <TableRow key={school.id}>
                  <TableCell className="font-medium">{school.schoolName}</TableCell>
                  <TableCell>{school.schoolNameEn}</TableCell>
                  <TableCell>
                    <Badge variant={school.gamificationEnabled ? 'default' : 'secondary'}>
                      {school.gamificationEnabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant={school.gamificationEnabled ? 'outline' : 'default'}
                      size="sm"
                      disabled={togglingSchoolId === school.id}
                      onClick={() => handleToggleSchool(school)}
                    >
                      {togglingSchoolId === school.id
                        ? 'Updating...'
                        : school.gamificationEnabled ? 'Disable' : 'Enable'}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Badge variant={school.leaderboardEnabled ? 'default' : 'secondary'}>
                      {school.leaderboardEnabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant={school.leaderboardEnabled ? 'outline' : 'default'}
                      size="sm"
                      disabled={togglingLeaderboardSchoolId === school.id || !school.gamificationEnabled}
                      onClick={() => handleToggleLeaderboard(school)}
                    >
                      {togglingLeaderboardSchoolId === school.id
                        ? 'Updating...'
                        : school.leaderboardEnabled ? 'Disable' : 'Enable'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pre-Gamification Preview */}
      {preview && (
        <div className="p-4 bg-muted m-2 rounded-2xl">
          <h3 className="text-lg font-semibold mb-4">Pre-Gamification Preview</h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-background rounded-xl p-4">
              <p className="text-sm text-muted-foreground">Gamification Start Month</p>
              <p className="text-xl font-semibold">{preview.gamificationStartMonth ?? '-'}</p>
            </div>
            <div className="bg-background rounded-xl p-4">
              <p className="text-sm text-muted-foreground">Total Records to Archive</p>
              <p className="text-xl font-semibold">{preview.totalRecordsToArchive ?? 0}</p>
            </div>
            <div className="bg-background rounded-xl p-4">
              <p className="text-sm text-muted-foreground">Total Affected Students</p>
              <p className="text-xl font-semibold">{preview.totalAffectedStudents ?? 0}</p>
            </div>
          </div>
          {preview.affectedStudents && preview.affectedStudents.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student Name</TableHead>
                  <TableHead>School Name</TableHead>
                  <TableHead>Record Count</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.affectedStudents.map((student) => (
                  <TableRow key={student.studentId}>
                    <TableCell>{student.studentName}</TableCell>
                    <TableCell>{student.schoolName}</TableCell>
                    <TableCell>{student.recordCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      {/* Recalculate Result Dialog */}
      <Dialog open={!!recalculateResult} onOpenChange={(open) => !open && setRecalculateResult(null)}>
        <DialogContent className="max-w-lg h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{applied ? 'Recalculation Applied' : 'Recalculation Preview'}</DialogTitle>
          </DialogHeader>
          {recalculateResult && (
            <div className="space-y-4 flex-1 min-h-0 flex flex-col">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted rounded-xl p-3">
                  <p className="text-sm text-muted-foreground">Total Students</p>
                  <p className="text-xl font-semibold">{recalculateResult.totalStudents ?? 0}</p>
                </div>
                <div className="bg-muted rounded-xl p-3">
                  <p className="text-sm text-muted-foreground">Changed Students</p>
                  <p className="text-xl font-semibold">{recalculateResult.changedStudents ?? 0}</p>
                </div>
              </div>
              {recalculateResult.changes && recalculateResult.changes.length > 0 && (
                <ScrollArea className="flex-1 min-h-0">
                  <div className="space-y-4">
                    {recalculateResult.changes.map((c) => (
                      <div key={c.studentId} className="bg-muted rounded-xl p-3 space-y-2">
                        <div className="flex justify-between items-baseline">
                          <p className="font-medium">{c.studentName}</p>
                          <p className="text-xs text-muted-foreground">{c.schoolName}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-xs text-muted-foreground">KP All-Time</p>
                            <p>{c.oldKpAllTime} → {c.newKpAllTime}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">GS All-Time</p>
                            <p>{c.oldGsAllTime} → {c.newGsAllTime}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">KP Month</p>
                            <p>{c.oldKpCurrentMonth} → {c.newKpCurrentMonth}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">GS Month</p>
                            <p>{c.oldGsCurrentMonth} → {c.newGsCurrentMonth}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
              {recalculateResult.changes?.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No score changes detected.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecalculateResult(null)}>
              Close
            </Button>
            {!applied && (
              <Button variant="destructive" disabled={applying} onClick={handleApplyRecalculate}>
                {applying ? 'Applying...' : 'Apply Changes'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingConfig} onOpenChange={(open) => !open && setEditingConfig(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit: {editingConfig?.configKey}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Value</Label>
              <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>Description</Label>
              <Input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingConfig(null)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ContentLayout>
  );
};

export default GamificationConfigPage;
